import { Component, Input, ViewEncapsulation, inject, ChangeDetectionStrategy } from '@angular/core';
import {LogoSizes} from '../pdf-page-header/pdf-page-header.data';
import {ProjectDetails} from '../../models';
import {TranslationService} from '../../services';
import {TranslateService} from '@ngx-translate/core';
import {findTenderCostsRange} from '../../utils';

@Component({
    selector: 'app-pdf-page-project-info',
    templateUrl: './pdf-page-project-info.component.html',
    styleUrls: ['./pdf-page-project-info.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PdfPageProjectInfoComponent {
    private translationService = inject(TranslationService);
    private translateService = inject(TranslateService);


    @Input() projectDetails: ProjectDetails;

    public logoSize = LogoSizes;

    public isEmpty(property: string): boolean {
        return !property || property.trim() === '';
    }

    public getTranslationWithFallback(keyPrefix: string, value: string): string {
        const translationKey = `${keyPrefix}.${value}`.trim();
        const translation = this.translateService.instant(translationKey);

        return translation === translationKey ? value : translation;
    }

    public tenderCostsLabel(): string {
        return findTenderCostsRange(this.projectDetails.projectInfo?.tenderCosts)?.label ?? '';
    }

    public isProjectAnalyisProject() : boolean {
        return this.projectDetails.projectType !== null || !!this.projectDetails.isPrmTwoProject;
    }

    public isLegacyProject() : boolean {
        return !(this.isProjectAnalyisProject());
    }

    public constructionCostsLabel() : string {
        if (this.isProjectAnalyisProject()) {
            return this.translateService.instant('PAGE_PROJECT_DETAILS.form.constructionCostsEstimate');
        }
        return this.projectDetails.projectInfo.constructionCostsLabel;
    }

    /**
     * Retrieve the full readable address
     * ( Used for the PDF details )
     */
    public getFullAddress(): string {
        let fullAddress = '';
        if ( this.projectDetails ) {
            if ( this.projectDetails.address ) {
                fullAddress += this.projectDetails.address;

                if ( this.projectDetails.city || this.projectDetails.country ) {
                    fullAddress += ', ';
                }
            }

            if ( this.projectDetails.city ) {
                fullAddress += this.projectDetails.city;

                if ( this.projectDetails.country ) {
                    fullAddress += ' ' + this.translationService.getCountryName( this.projectDetails.country );
                }
            }
        }

        return fullAddress;
    }

    public getProjectVersion(): string {
        if (this.projectDetails.projectVersion) {
            const version = this.projectDetails.projectVersion;
            if (version.majorVersion === 1 && version.minorVersion === 0) {
                return this.translateService.instant('STEPS.2');
            }
            return `${version.majorVersion}.${version.minorVersion}`;
        }
        return '';
    }

    get workTypes(): string {
        return this.projectDetails?.projectAttributesValues?.workTypes;
    }

    get buildingClassifications(): string {
        return this.projectDetails?.projectAttributesValues?.buildingClassifications;
    }

    get buildingSubClassifications(): string {
        return this.projectDetails?.projectAttributesValues?.buildingSubClassifications;
    }

    get constructionCosts(): number {
        const answer = this.projectDetails?.projectAnalysisAnswers?.getAnswerByPitTag('mandate.construction_costs');
        if (answer) {
            if (answer.answerAsInteger) {
                return answer.answerAsInteger;
            }
            if (answer.answerAsPercentage) {
                return answer.answerAsPercentage;
            }
        }
        // fall back, possibly obsolete
        return this.projectDetails?.projectInfo?.constructionCosts;
    }
}
